import React from 'react';
import ReactDOM from 'react-dom/client';

import { AppState, Auth0Provider, Auth0ProviderOptions } from '@auth0/auth0-react';

import { fetchServerTenant } from 'api/tenantDirectory';
import { ConfigState } from 'store/config/types';
import { getTenantFromCookie } from 'utils/auth';
import { SubdomainClientInfo, subdomainMap } from 'types/general.types';
import { getDomainAndSubdomain, setMetaData } from 'utils/domain';

import App from './App';
import './styles/index.scss';

export interface IData {
  tenant: string;
  loading: boolean;
  domain: string;
  clientId: string;
  clientInfo?: SubdomainClientInfo | null;
}

// Fail loudly at startup when the Auth0 env vars are missing — Auth0Provider
// otherwise throws a cryptic error deep inside the SDK on first login attempt.
const requireEnv = (name: string): string => {
  const value = process.env[name];
  if (!value) {
    throw new Error(`Missing required environment variable ${name} — copy .env.example and fill it in.`);
  }
  return value;
};

const getBaseConfiguration = async (): Promise<ConfigState> => {
  const tenant = getTenantFromCookie();
  const { subdomain } = getDomainAndSubdomain();
  // Server-driven branding (tenant_branding): resolves theme + Auth0 org id by subdomain
  // with no frontend deploy. Falls back to the bundled subdomainMap/themes.json below.
  const serverTenant = await fetchServerTenant(subdomain.toLowerCase());
  setMetaData(
    subdomain,
    serverTenant ? { favicon: serverTenant.theme.faviconUrl ?? undefined, title: serverTenant.displayName } : undefined,
  );
  const clientInfo = subdomain ? subdomainMap[subdomain.toLowerCase()] : null;

  return {
    tenant,
    loading: false,
    domain: requireEnv('REACT_APP_AUTH0_DOMAIN'),
    clientId: requireEnv('REACT_APP_AUTH0_CLIENT_ID'),
    clientInfo,
    serverTenant,
  };
};

const onRedirectCallback = (appState?: AppState) => {
  window.history.replaceState({}, document.title, appState?.targetUrl ?? window.location.pathname);
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

getBaseConfiguration().then(config => {
  const authParams: Auth0ProviderOptions = {
    domain: config.domain,
    clientId: config.clientId,
    authorizationParams: {
      redirect_uri: window.location.origin,
      organization: config.serverTenant?.auth0OrgId || config.clientInfo?.orgId,
      audience: requireEnv('REACT_APP_AUTH0_AUDIENCE'),
      scope: 'profile openid email',
    },
    onRedirectCallback,
  };

  root.render(
    <React.StrictMode>
      <Auth0Provider {...authParams}>
        <App preloadedState={{ config }} />
      </Auth0Provider>
    </React.StrictMode>,
  );
});
